/**
 * Rutinas centralizadas de validación de entrada.
 *
 * Todo valor que provenga de una fuente no confiable (formularios, parámetros
 * de ruta, respuestas de la API, datos de diálogos) y que se use para construir
 * una URL, un encabezado o un parámetro de consulta debe validarse aquí antes
 * de usarse. La estrategia es lista blanca: se define qué se acepta y se
 * rechaza todo lo demás.
 *
 * Nota: estas validaciones son del lado del cliente y deben estar duplicadas en
 * la API. La validación de front-end no sustituye la del servidor.
 */

/** Patrones permitidos por tipo de dato. */
export const PATRONES_VALIDACION = {
  /** Número de activo: alfanumérico con espacios, punto, guion, guion bajo y diagonal. */
  numeroActivo: /^[A-Za-z0-9][A-Za-z0-9 ._/-]{0,49}$/,
  /** Identificadores numéricos enteros positivos. */
  identificadorNumerico: /^[0-9]{1,12}$/,
  /** Claves de catálogo: sólo letras, dígitos, guion y guion bajo. */
  claveCatalogo: /^[A-Za-z0-9_-]{1,50}$/,
  /** Cantidades decimales positivas con hasta dos decimales (litros, horómetro). */
  decimalPositivo: /^\d{1,10}(?:\.\d{1,2})?$/,
  /** Fecha en formato ISO 8601 (UTC), como la produce Date.toISOString(). */
  fechaIso: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z?$/,
  /** Códigos IATA de estación: tres letras. */
  codigoIata: /^[A-Za-z]{3}$/,
  /** Evidencia fotográfica: data URL de imagen png o jpeg en base64. */
  imagenDataUrl: /^data:image\/(?:png|jpe?g);base64,[A-Za-z0-9+/]+={0,2}$/
} as const;

/**
 * Valida una cadena contra un patrón de lista blanca y devuelve el valor
 * recortado. Lanza error cuando el valor no cumple el formato esperado.
 *
 * @param valor Valor recibido de una fuente no confiable.
 * @param patron Expresión regular de lista blanca.
 * @param campo Nombre del campo, usado en el mensaje de error.
 */
export function validarCadena(valor: unknown, patron: RegExp, campo: string): string {
  if (typeof valor !== 'string') {
    throw new Error(`El valor de ${campo} no es válido.`);
  }

  const limpio = valor.trim();
  if (!patron.test(limpio)) {
    throw new Error(`El valor de ${campo} no cumple el formato permitido.`);
  }

  return limpio;
}

/**
 * Valida un número de activo antes de enviarlo a la API.
 *
 * @throws Error si el valor no cumple el formato esperado.
 */
export function validarNumeroActivo(valor: unknown): string {
  return validarCadena(valor, PATRONES_VALIDACION.numeroActivo, 'número de activo');
}

/** Indica si un número de activo cumple el formato permitido, sin lanzar error. */
export function esNumeroActivoValido(valor: unknown): boolean {
  return typeof valor === 'string' && PATRONES_VALIDACION.numeroActivo.test(valor.trim());
}

/** Indica si el valor cumple un patrón de la lista blanca, sin lanzar error. */
export function cumplePatron(valor: unknown, patron: RegExp): boolean {
  return typeof valor === 'string' && patron.test(valor.trim());
}

/**
 * Indica si el valor es un data URL de imagen png o jpeg. Se usa antes de
 * transmitir evidencia fotográfica para no enviar contenido de otro tipo.
 */
export function esImagenDataUrlValida(valor: unknown): boolean {
  return typeof valor === 'string' && PATRONES_VALIDACION.imagenDataUrl.test(valor.replace(/\s+/g, ''));
}

/**
 * Convierte un valor a entero no negativo. Devuelve 0 cuando el valor no es un
 * número utilizable, para no propagar `NaN`, `null` ni objetos al backend.
 */
export function aEnteroNoNegativo(valor: unknown): number {
  const numero = typeof valor === 'number' ? valor : Number.parseInt(String(valor ?? ''), 10);
  return Number.isFinite(numero) && numero > 0 ? Math.trunc(numero) : 0;
}

/**
 * Normaliza un texto para transmisión: recorta espacios y limita la longitud.
 * Cualquier valor que no sea cadena se convierte en cadena vacía.
 */
export function aTextoAcotado(valor: unknown, longitudMaxima: number): string {
  if (typeof valor !== 'string') {
    return '';
  }
  return valor.trim().slice(0, longitudMaxima);
}
